import { DataCenterFinancialSnapshot } from '../../financial/DataCenterFinancialSnapshot';
/**
 * MonteCarloBandsSection (FIN-2) — P10 / P50 / P90 NPV bands + P(NPV>0)
 * (editorial).
 *
 * ⚠ BASIS: this band comes from the BENCHMARK PATHWAY comparison, not from the
 * project's own financial model. The pathway engine prices technology options
 * against a flat value-of-served-load credit, so its NPV distribution is a
 * statement about "what would a grid / gas / SMR option of this size cost",
 * NOT an uncertainty band around the `headline.npvUsd` printed elsewhere in
 * this report. `monteCarlo.basis` names it and the section labels it — an
 * unlabelled band sitting beside the project NPV reads as its confidence
 * interval, which is how a −$694M band ended up next to a +$611M headline.
 *
 * READ-ONLY over `report.financialAnalysis.monteCarlo` (persisted bands). Renders the
 * P10..P90 NPV band as a themed horizontal whisker around the P50 point, the
 * P(positive NPV) toned positive/warning/negative via roleColor, and the
 * seed/run methodology stamp. Chart chrome is THEME-AWARE (resolveChartGridChrome
 * + roleColor + chartPalette); no raw oklch in chart CSS (PDF-export safe).
 * NEVER re-runs the Monte-Carlo — it visualises the persisted bands only.
 *
 * W2a port: the hand-rolled percentile strip → EditorialKpiRail; the recharts
 * whisker chart is KEPT (in ChartFrame) and wrapped in an EditorialExhibit with
 * the seed/runs methodology stamp as its numbered EditorialFigureCaption (the
 * stamp stays as plain small print when only P(NPV>0) exists and there is no
 * band figure to caption). Identical data + formatting via financialFormat.
 */
import React from 'react';
import {
  ComposedChart, Bar, ErrorBar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { ChartFrame } from '../../../ui/ChartFrame';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { resolveChartGridChrome } from '../../../../lib/reportChartGrid';
import { useFigureNumber } from '../../report/figureCounter';
import { EditorialExhibit, EditorialKpiRail } from '../../report/editorial';
import { ReportSourceNote } from '../../report/ReportSourceNote';
import { FIN_MONO, fmtProbabilityPct, fmtUsdMillions } from '../../../../lib/reportRendering/financialFormat';

function isNum(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export const MonteCarloBandsSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const figNo = useFigureNumber('financial:monteCarloBand');
  const mc = report.financialAnalysis?.monteCarlo;

  const p10 = mc ? (isNum(mc.npvP10) ? mc.npvP10 : null) : null;
  const p50 = mc ? (isNum(mc.npvP50) ? mc.npvP50 : null) : null;
  const p90 = mc ? (isNum(mc.npvP90) ? mc.npvP90 : null) : null;
  const pPos = mc ? (isNum(mc.pPositiveNpv) ? mc.pPositiveNpv : null) : null;

  const hasBand = p50 != null && (p10 != null || p90 != null);
  const hasData = hasBand || pPos != null;

  const grid = resolveChartGridChrome(theme);
  const axisInk = roleColor(theme, 'muted');

  // P(NPV>0) tone: ≥0.5 positive, ≥0.3 warning, else negative (mirrors the band).
  const probTone = pPos == null
    ? 'muted'
    : pPos >= 0.5 ? 'positive' : pPos >= 0.3 ? 'warning' : 'negative';
  const probInk = roleColor(theme, probTone as 'positive' | 'warning' | 'negative' | 'muted');

  const lo = p10 ?? p50 ?? 0;
  const hi = p90 ?? p50 ?? 0;
  const point = p50 ?? 0;
  const below = Math.max(0, point - Math.min(lo, hi));
  const above = Math.max(0, Math.max(lo, hi) - point);
  const bandFill = roleColor(theme, 'accent');
  const chartRows = hasBand
    ? [{ label: 'NPV', point, error: [below, above] as [number, number], p10: Math.min(lo, hi), p90: Math.max(lo, hi) }]
    : [];

  // WHY the band is missing, when it is. A pathway comparison that failed to
  // load must not read as "no evidence" — that is a system fault, not a finding.
  const pathwayStatus = report.financialAnalysis?.pathwayStatus;
  const absentReason = hasData
    ? null
    : pathwayStatus === 'load-failed'
      ? 'The pinned pathway comparison could not be loaded when this report was generated, so no benchmark band is available. Regenerate the report to retry.'
      : 'No pathway comparison is pinned for this site, so there is no benchmark band to report. The project economics above are unaffected.';

  return (
    // `emptyMessage` — NOT a child. `SectionShell` renders children ONLY when
    // `hasData` is true, so an explanation rendered as a child of a shell whose
    // `hasData` is false is unreachable by construction: the reader got the
    // generic "requires inputs that were not surfaced in the SED" instead.
    //
    // That generic line is FALSE here in both directions. No pinned comparison
    // has nothing to do with the SED; and a comparison we tried to load and
    // lost is a SYSTEM FAULT, not missing evidence — blaming the SED for it is
    // exactly the misattribution `pathwayStatus` was added to prevent. The
    // status was computed, stamped and then discarded at the last inch.
    <SectionShell label={label} hasData={hasData} emptyMessage={absentReason ?? undefined}>
      {!hasData ? null : (
        <div className="space-y-3">
          {/* BASIS LABEL — must precede the numbers. */}
          <p
            className="text-[11px]"
            style={{ color: roleColor(theme, 'muted') }}
            data-testid="financial-mc-basis-note"
          >
            <strong style={{ color: roleColor(theme, 'ink') }}>Benchmark-pathway basis.</strong>{' '}
            These percentiles describe the modelled economics of
            {mc?.anchorPathwayName ? ` the ${mc.anchorPathwayName} option` : ' the leading benchmark supply option'}
            {' '}priced against a standard value-of-served-load credit – they are a technology comparison, not an
            uncertainty band around this project&rsquo;s own NPV.
          </p>
          {/* P10/P50/P90 percentile rail. Carrier div holds the testid
              (EditorialKpiRail has no testid prop). */}
          <div data-testid="financial-mc-percentile-rail">
            <EditorialKpiRail
              theme={theme}
              items={[
                { key: 'P10', value: fmtUsdMillions(p10) },
                { key: 'P50 (median)', value: fmtUsdMillions(p50) },
                { key: 'P90', value: fmtUsdMillions(p90) },
              ]}
            />
          </div>

          {hasBand ? (
            <EditorialExhibit
              theme={theme}
              span="columns"
              captionNumber={figNo}
              caption={
                <>
                  Monte-Carlo over the benchmark pathway
                  {mc?.anchorPathwayName ? ` (${mc.anchorPathwayName})` : ''}, fixed seed {mc?.seed ?? '–'} over{' '}
                  {mc?.runs ?? '–'} runs (reproducible). Whisker spans the P10–P90 NPV band around the P50 median.
                  Benchmark-technology basis – not a confidence interval on this project&rsquo;s NPV.
                </>
              }
            >
              {/* Carrier div holds the testid + export-capture hooks (the exhibit
                  figure owns the span; EditorialExhibit has no testid prop). */}
              <div
                className="w-full"
                data-testid="financial-mc-band-chart"
                data-export-capture=""
                data-export-id="financial-mc-band-chart"
                data-export-title="Monte-Carlo NPV Band"
              >
                <ChartFrame exportId="financial-mc-band-chart" traceName="report:financial-mc-band" minWidth={100} aspect={0.32} minHeight={140} maxHeight={220}>
                <ComposedChart layout="vertical" data={chartRows} margin={{ top: 12, right: 24, bottom: 8, left: 8 }}>
                  <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke={grid.grid} />
                  <XAxis
                    type="number"
                    tickFormatter={(v: number) => `$${Math.round(v / 1_000_000)}M`}
                    tick={{ fontSize: 11, fill: axisInk }}
                    stroke={grid.axis}
                  />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: axisInk }} stroke={grid.axis} width={48} />
                  <ReferenceLine x={0} stroke={grid.axis} />
                  <Tooltip
                    cursor={{ fill: 'transparent' }}
                    content={({ payload }) => {
                      const r = payload?.[0]?.payload as (typeof chartRows)[number] | undefined;
                      if (!r) return null;
                      if (report.financialAnalysis?.modelKind === 'data-center-capacity-fee') return <SectionShell label={label} hasData><DataCenterFinancialSnapshot stamp={report.financialAnalysis} section="monteCarloBands" /></SectionShell>;

  return (
                        <div className="px-2 py-1 text-xs" style={{ background: roleColor(theme, 'subtleBg'), border: `1px solid ${roleColor(theme, 'hair')}` }}>
                          <div className="font-bold" style={{ color: roleColor(theme, 'ink') }}>NPV band</div>
                          <div style={{ color: roleColor(theme, 'muted') }}>P50 {fmtUsdMillions(r.point)}</div>
                          <div style={{ color: roleColor(theme, 'muted') }}>P10–P90 {fmtUsdMillions(r.p10)} – {fmtUsdMillions(r.p90)}</div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="point" barSize={12} radius={[2, 2, 2, 2]} isAnimationActive={false}>
                    <Cell fill={bandFill} />
                    <ErrorBar dataKey="error" width={8} strokeWidth={2} stroke={roleColor(theme, 'muted')} direction="x" />
                  </Bar>
                </ComposedChart>
                </ChartFrame>
              </div>
            </EditorialExhibit>
          ) : null}

          {pPos != null ? (
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] font-bold uppercase tracking-widest" style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}>
                P(NPV &gt; 0)
              </span>
              <span className="text-lg font-bold tabular-nums" style={{ fontFamily: FIN_MONO, color: probInk }}>
                {fmtProbabilityPct(pPos)}
              </span>
            </div>
          ) : null}

          {!hasBand ? (
            // No band figure to caption — keep the methodology stamp as plain
            // small print so seed/runs provenance never disappears.
            <ReportSourceNote testId="financial-mc-methodology-note">
              Monte-Carlo over the benchmark pathway, fixed seed {mc?.seed ?? '–'} over {mc?.runs ?? '–'} runs
              (reproducible). Benchmark-technology basis – not a confidence interval on this project&rsquo;s NPV.
            </ReportSourceNote>
          ) : null}
        </div>
      )}
    </SectionShell>
  );
};
